"use client";

// New pricing page (ticket: "Implement new pricing page"). Renders the four
// ticket tiers (Free / Pro / Grow / Scale) from lib/pricing/credit-tiers — each
// PAID tier carries a live credit dropdown (price updates as you change the
// amount), with a monthly/annual toggle (annual = 2 months free). Structure
// mirrors panda_pricing.html: hero + toggle → cards → "no lock-in" line → FAQ →
// bottom prompt box. NO comparison table (per Aaron's note). Styling reuses the
// existing pricing design system so it matches the rest of the site.
//
// Checkout wiring + the plan-key rename/migration are a follow-up step; this
// component is presentation + the live price math only.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Check, ChevronDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { PromptAttachments } from "@/components/prompt-attachments";
import {
  useTypewriterPlaceholder,
  PROMPT_HINT_PHRASES,
} from "@/hooks/use-typewriter-placeholder";
import { setPendingAttachments } from "@/lib/pending-attachments";
import { persistPendingAttachments } from "@/lib/pending-attachments-idb";
import {
  PRICING_TIERS,
  PAID_TIER_IDS,
  PLAN_CREDIT_LADDER,
  PLAN_BASE_CREDITS,
  ANNUAL_MONTHS_CHARGED,
  tierPerMonthCents,
  tierAnnualCents,
  centsToDollars,
  type PaidTierId,
  type BillingCadence,
} from "@/lib/pricing/credit-tiers";
import { useBillingCadence } from "@/lib/billing/cadence-preference";
import { MobilePlanCompare } from "@/components/pricing/mobile-plan-compare";
import {
  trackPlanCtaClicked,
  trackPricingToggleSwitched,
  type PricingSurface,
} from "@/lib/analytics";

interface PricingTiersSectionProps {
  user?: { email?: string } | null;
  /** Opens the login/signup modal for anonymous visitors selecting a paid plan. */
  openLogin: (nextPath: string) => void;
  postAuthPath?: string;
  /** The signed-in user's active plan; the matching card locks to "Current plan". */
  currentPlan?: string;
  /**
   * Has that plan actually been read from the server yet?
   *
   * REQUIRED reading, not a nicety: `currentPlan` arrives a beat after the page
   * paints, and until it does, `isCurrent()` answers false for EVERY card — so
   * an active Pro user saw a live "Upgrade" CTA on the plan they are already
   * on, and clicking it inside that window opened a real checkout for a second
   * subscription. Defaults true so callers that already know the plan (or have
   * no user to know it for) are unchanged.
   */
  planResolved?: boolean;
  /** Signed-in checkout handler (tier + chosen credits + cadence). Wired in the
   *  billing-internals follow-up; until then the card falls back to login. */
  onChoosePlan?: (
    tier: PaidTierId,
    credits: number,
    cadence: BillingCadence,
  ) => void;
  /** "Start free" / bottom prompt → homepage prompt box. */
  onStartForFree?: () => void;
  anchorId?: string;
}

/**
 * The plan-card accent: the highlighted card's border, its "Most popular"
 * ribbon, and the annual toggle.
 *
 * emerald-700 since PANDA-2025. It was #FF5606 — brand orange — which made
 * "Most popular" and the plan we most want chosen wear the same colour as a
 * destructive confirm, on the one page whose entire job is an upgrade. White
 * text on emerald-700 is 5.5:1, so the 12px uppercase ribbon label clears the
 * 4.5:1 it needs; don't lighten this without re-checking that.
 */
const ACCENT = "#047857";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Can I start for free?",
    a: "Yes. No credit card needed. Build your first app free and upgrade only when you're ready to launch.",
  },
  {
    q: "How do credits work?",
    a: "One credit pool powers everything — building apps and running Grow. Pick the monthly credit amount that fits on any paid plan, top up any time, and unused credits roll over.",
  },
  {
    q: "Can I cancel any time?",
    a: "Yes. No contracts, no lock-in. Cancel any time and your app stays live until the end of your billing period.",
  },
];

export function PricingTiersSection({
  user,
  openLogin,
  postAuthPath = "/pricing",
  currentPlan,
  planResolved = true,
  onChoosePlan,
  onStartForFree,
  anchorId,
}: PricingTiersSectionProps) {
  const router = useRouter();
  // Shared + persisted across every pricing surface (see cadence-preference).
  const [billing, setBilling] = useBillingCadence();

  function switchBilling(next: BillingCadence) {
    if (next === billing) return;
    const from = billing;
    setBilling(next);
    trackPricingToggleSwitched({
      surface: "pricing_page",
      from_cadence: from,
      to_cadence: next,
    });
  }
  // Each paid card has its own credit selection (Lovable-style dropdown), seeded
  // to that plan's OWN included allowance — the cheapest rung it can select.
  // (Previously every card seeded to one shared 650.)
  const [credits, setCredits] = useState<Record<PaidTierId, number>>({
    pro: PLAN_BASE_CREDITS.pro,
    grow: PLAN_BASE_CREDITS.grow,
    scale: PLAN_BASE_CREDITS.scale,
  });
  const [promptText, setPromptText] = useState("");
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // Lovable-style animated hint, same as the homepage boxes. Paused (holds the
  // first phrase) once the user has typed, since the placeholder isn't shown.
  const animatedPhrase = useTypewriterPlaceholder(
    PROMPT_HINT_PHRASES,
    promptText.trim().length === 0,
  );

  // Auto-grow the prompt textarea so the box expands as lines wrap — min-height
  // on the box is only a floor (matches the homepage composer behavior).
  useEffect(() => {
    const ta = promptRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [promptText]);

  // "This is your plan" and "we have not asked yet" are different answers, and
  // reading the second as the first is the whole bug. While unresolved NO card
  // claims to be current — which also stops the Free card briefly telling a Pro
  // user that Free is their plan, the same mistake pointing the other way.
  const isCurrent = (id: string) =>
    planResolved &&
    !!currentPlan &&
    currentPlan.toLowerCase() === id.toLowerCase();

  // Free-tier card CTA ("Start free"). The page passes `onStartForFree`, which
  // routes through the shared start-free helper — signed-out visitors open
  // signup, signed-in visitors go into the app. The `?focus=prompt` branch is a
  // documented fallback ONLY for a caller that mounts this section without the
  // handler; no shipped surface hits it, so a "Start free" click never merely
  // refocuses the homepage prompt in prod. See lib/marketing/start-free.ts.
  function goToBuild() {
    if (onStartForFree) onStartForFree();
    else router.push("/?focus=prompt");
  }

  // `surface` separates a buy made from the plan CARDS from one made in the
  // mobile comparison. Without it both report "pricing_page" and there is no way
  // to tell whether the comparison actually helps people decide — which the
  // mobile-comparison ticket asks to be able to measure.
  function handleChoose(
    tier: PaidTierId,
    surface: PricingSurface = "pricing_page",
  ) {
    // Before the auth gate, so a signed-out click still records the plan +
    // cadence the visitor wanted.
    trackPlanCtaClicked({
      surface,
      plan: tier,
      cadence: billing,
    });
    if (!user) {
      openLogin(postAuthPath);
      return;
    }
    onChoosePlan?.(tier, credits[tier], billing);
  }

  async function submitPrompt() {
    const text = promptText.trim();
    // Carry any picked files to the homepage build via the same pending-
    // attachments channel the homepage restores from, then forward the typed
    // prompt with ?prefill so the hero box lands pre-filled.
    if (attachFiles.length > 0) {
      setPendingAttachments(attachFiles);
      try {
        await persistPendingAttachments(attachFiles);
      } catch {
        // Best-effort — a persistence hiccup shouldn't block navigation.
      }
    }
    router.push(text ? `/?prefill=${encodeURIComponent(text)}` : "/?focus=prompt");
  }

  return (
    <section
      id={anchorId}
      className="relative bg-background py-16 dark:bg-background sm:py-24"
    >
      <div className="mx-auto max-w-[1264px] px-4 sm:px-8">
        {/* Hero */}
        <div className="flex flex-col items-center gap-3 text-center">
          {/* "Launch when ready." is the emphasised half, so it starts the
              second line instead of being split across the wrap. Left to
              itself the headline is one run of text and breaks wherever the
              box runs out — "Start free. Launch" / "when ready." on every
              phone between ~356px and ~476px, and again at 640–775px (iPad
              portrait) once the type steps up to 48px. Hence the explicit
              break, hidden from 800px up where the whole line fits anyway.
              The mobile type is fluid because at 36px the phrase needs 347px
              and a 360/375px phone only has 328/343px — without the taper it
              would start on its own line but split again ("Launch when" /
              "ready."). 9.2vw keeps it one line down to ~304px; the 36px cap
              (reached at ~391px) preserves the designed size everywhere else. */}
          <h1 className="font-[family-name:var(--font-rubik),sans-serif] text-[clamp(28px,9.2vw,36px)] font-medium leading-[1.22] text-foreground sm:text-[48px] sm:leading-[56px]">
            Start free.{" "}
            <br className="min-[800px]:hidden" />
            {/* Green since PANDA-2025, and the same utility the homepage hero's
                accent word uses — the two headlines sit one click apart. */}
            <span className="bg-panda-gradient-success-text bg-clip-text text-transparent">
              Launch when ready.
            </span>
          </h1>
          <p className="max-w-[40ch] font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#475569] dark:text-muted-foreground">
            Build your app. Get customers. Upgrade when you&apos;re ready to
            launch.
          </p>
        </div>

        {/* Monthly / annual toggle — matches the HTML mockup:
            "Monthly  [switch]  Annually  (2 months free)". The switch is
            emerald-600 rather than the mockup's pink; it was brand orange
            until PANDA-2025, which made choosing the cheaper billing period
            look like a warning. */}
        <div className="mt-8 flex items-center justify-center gap-3 font-[family-name:var(--font-rubik),sans-serif]">
          <span
            className={cn(
              "text-sm font-medium transition-colors",
              billing === "monthly" ? "text-foreground" : "text-muted-foreground",
            )}
          >
            Monthly
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={billing === "annual"}
            aria-label="Bill annually (2 months free)"
            onClick={() => switchBilling(billing === "annual" ? "monthly" : "annual")}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              billing === "annual" ? "bg-emerald-600" : "bg-muted-foreground/30",
            )}
          >
            <span
              className={cn(
                "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                billing === "annual" ? "translate-x-[22px]" : "translate-x-0.5",
              )}
            />
          </button>
          <span
            className={cn(
              "text-sm font-medium transition-colors",
              billing === "annual" ? "text-foreground" : "text-muted-foreground",
            )}
          >
            Annually
          </span>
          <span className="rounded-full bg-[#12B76A]/15 px-2.5 py-1 text-xs font-semibold text-[#12B76A]">
            2 months free
          </span>
        </div>

        {/* Jump straight to the comparison. Without it the only route to the
            table is scrolling past four full-height cards — the effort the
            mobile-comparison ticket is about. Hidden on desktop, where the
            cards are already side by side. */}
        <div className="mt-6 flex justify-center lg:hidden">
          <a
            href="#compare-plans"
            className="inline-flex h-9 items-center rounded-full border border-border px-4 font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium text-foreground transition-colors hover:bg-card"
          >
            Compare plans side by side
          </a>
        </div>

        {/* Cards — Free + 3 paid (credit dropdown) */}
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Free */}
          <TierCard
            tierId="free"
            isCurrent={isCurrent("free")}
            priceNode={
              <div className="flex items-baseline">
                <span className="font-[family-name:var(--font-rubik),sans-serif] text-[40px] font-medium leading-12 text-foreground">
                  $0
                </span>
                <span className="ml-1 font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
                  forever
                </span>
              </div>
            }
            onCta={goToBuild}
          />

          {PAID_TIER_IDS.map((tier) => {
            const def = PRICING_TIERS[tier];
            const chosen = credits[tier];
            const perMonth = tierPerMonthCents(tier, chosen, billing);
            const annualYear = tierAnnualCents(tier, chosen);
            return (
              <TierCard
                key={tier}
                tierId={tier}
                isCurrent={isCurrent(tier)}
                awaitingPlan={!planResolved}
                priceNode={
                  <>
                    <div className="flex items-baseline">
                      <span className="font-[family-name:var(--font-rubik),sans-serif] text-[40px] font-medium leading-12 text-foreground">
                        {centsToDollars(perMonth)}
                      </span>
                      <span className="ml-1 font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
                        /month
                      </span>
                    </div>
                    <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
                      {billing === "annual"
                        ? `${centsToDollars(annualYear)}/year billed annually`
                        : `${centsToDollars(perMonth * ANNUAL_MONTHS_CHARGED)}/year if billed annually`}
                    </p>
                    {!def.ratesConfirmed && (
                      <p className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 text-amber-600 dark:text-amber-400">
                        Indicative pricing — final rate to be confirmed
                      </p>
                    )}
                  </>
                }
                creditSelect={
                  <label className="mt-1 block">
                    <span className="sr-only">Monthly credits for {def.name}</span>
                    <div className="relative">
                      <select
                        value={chosen}
                        onChange={(e) =>
                          setCredits((prev) => ({
                            ...prev,
                            [tier]: Number(e.target.value),
                          }))
                        }
                        className="h-10 w-full appearance-none rounded-lg border border-border bg-background px-3 pr-9 font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                      >
                        {PLAN_CREDIT_LADDER[tier].map((c: number) => (
                          <option key={c} value={c}>
                            {c.toLocaleString("en-US")} credits / month
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    </div>
                  </label>
                }
                onCta={() => handleChoose(tier)}
              />
            );
          })}
        </div>

        {/* No lock-in line */}
        <p className="mt-8 text-center font-[family-name:var(--font-rubik),sans-serif] text-sm text-[#475569] dark:text-muted-foreground">
          No contracts. No lock-in. Cancel any time.
        </p>

        {/* Side-by-side comparison, phones and tablets only. Desktop keeps the
            four cards in one row, where comparing already works and where
            Product asked for no comparison table. */}
        <MobilePlanCompare
          billing={billing}
          currentPlan={currentPlan}
          onChoosePaid={(tier) => handleChoose(tier, "compare_plans")}
          onStartFree={goToBuild}
        />

        {/* FAQ */}
        <div className="mx-auto mt-20 max-w-[680px]">
          <h2 className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-medium text-foreground sm:text-[28px]">
            Quick answers
          </h2>
          <div className="mt-6">
            {FAQ.map((item) => (
              <div
                key={item.q}
                className="border-b border-[#E2E8F0] py-4 last:border-b-0 dark:border-border"
              >
                <p className="font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground">
                  {item.q}
                </p>
                <p className="mt-1.5 font-[family-name:var(--font-rubik),sans-serif] text-sm leading-6 text-[#475569] dark:text-muted-foreground">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom prompt box — start building straight from pricing, matching
            the homepage prompt entry. */}
        {/* p-6 below `sm`, not p-8: 32px of padding a side costs the card 64px
            of its ~375px, which is what squeezed the trust row below. Unchanged
            from `sm` up, where the card was never tight. */}
        <div className="mt-20 rounded-[28px] border border-[#E2E8F0] bg-card p-6 text-center dark:border-border sm:p-12">
          <h2 className="font-[family-name:var(--font-rubik),sans-serif] text-[28px] font-medium leading-9 text-foreground sm:text-[32px]">
            Build your app and get customers.
          </h2>
          <p className="mt-2 font-[family-name:var(--font-rubik),sans-serif] text-sm text-[#475569] dark:text-muted-foreground">
            Free to start. Build your first app today.
          </p>
          {/* Same prompt box as the homepage bottom section — tall card with a
              single-line placeholder, "+" attachments, and a circular gradient
              submit arrow. Submitting forwards to the homepage hero, pre-filled. */}
          <div className="mx-auto mt-6 flex min-h-[125px] w-full max-w-[448px] flex-col rounded-[16px] border border-border bg-background p-4 text-left shadow-[0_0_80px_-28px_rgba(140,90,255,0.35)] transition-shadow focus-within:border-emerald-600/60 focus-within:shadow-[0_0_0_4px_rgba(5,150,105,0.15)]">
            <textarea
              ref={promptRef}
              rows={1}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void submitPrompt();
                }
              }}
              placeholder={animatedPhrase}
              aria-label="Describe the app you want to build"
              className="mb-3 block min-h-[28px] w-full resize-none overflow-hidden border-0 bg-transparent px-1 pt-1 font-[family-name:var(--font-geist),sans-serif] text-[17px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/70 dark:placeholder:text-white/90"
            />
            <div className="mt-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <PromptAttachments
                  files={attachFiles}
                  onChange={setAttachFiles}
                  buttonClassName="h-11 w-11"
                />
              </div>
              <button
                type="button"
                onClick={() => void submitPrompt()}
                disabled={promptText.trim().length === 0}
                aria-label="Build my app"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-panda-gradient-success text-white shadow-[0_10px_24px_-8px_rgba(0,122,85,0.55)] transition-transform hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M12 19V5M5 12l7-7 7 7"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {/* Trust row. As one <p> at a fixed 12px this measured 335px against
              334px of card interior on a 430px phone — over by a single pixel,
              which is exactly why exactly one word fell — and over by 41px at
              390px and 71px at 360px. Fixed the same way as the homepage
              reassurance row (app/home-client.tsx): each claim is its own
              non-breaking span, so a line can only ever break BETWEEN claims —
              never stranding a word — and the font and separator gaps scale
              with the viewport down to the same 10px floor, which keeps all
              three claims on one readable line at 360px and up. "needed"
              was dropped from the first claim to buy the width back; "Free
              plan available" stays whole, which is the point of the fix. */}
          <div
            data-testid="pricing-cta-trust-row"
            className="mt-3 flex max-w-full flex-wrap items-baseline justify-center gap-x-[clamp(3px,1vw,6px)] gap-y-1 font-[family-name:var(--font-rubik),sans-serif] text-[clamp(10px,2.9vw,12px)] text-[#77869D] dark:text-muted-foreground"
          >
            <span className="whitespace-nowrap">No credit card</span>
            <span aria-hidden className="opacity-60">
              ·
            </span>
            <span className="whitespace-nowrap">Cancel any time</span>
            <span aria-hidden className="opacity-60">
              ·
            </span>
            <span className="whitespace-nowrap">Free plan available</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function TierCard({
  tierId,
  isCurrent,
  awaitingPlan = false,
  priceNode,
  creditSelect,
  onCta,
}: {
  tierId: keyof typeof PRICING_TIERS;
  isCurrent: boolean;
  /** The account's plan has not been read yet, so this CTA cannot know whether
   *  it would be a purchase or a duplicate. Held rather than guessed. */
  awaitingPlan?: boolean;
  priceNode: React.ReactNode;
  creditSelect?: React.ReactNode;
  onCta: () => void;
}) {
  const def = PRICING_TIERS[tierId];
  return (
    <div
      className={cn(
        "relative flex flex-col overflow-hidden rounded-2xl border bg-card",
        def.highlighted
          ? "bg-emerald-50 dark:bg-emerald-500/[0.08]"
          : "border-border",
      )}
      style={def.highlighted ? { borderColor: ACCENT } : undefined}
    >
      {def.highlighted && (
        <div
          className="flex h-7 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium uppercase leading-4 text-white"
          style={{ background: ACCENT }}
        >
          Most popular
        </div>
      )}
      <div className="flex flex-1 flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <p className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-medium leading-8 text-foreground">
            {def.name}
          </p>
          <p className="min-h-[40px] font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
            {def.tagline}
          </p>
        </div>

        <div className="flex flex-col gap-1">{priceNode}</div>

        {creditSelect}

        <button
          type="button"
          disabled={isCurrent || awaitingPlan}
          aria-current={isCurrent ? "true" : undefined}
          // The label does not change while we wait. Swapping it for "Loading…"
          // would move the CTA's own text under the cursor for a second and
          // make a settled page look broken; `aria-busy` says the same thing to
          // anyone who needs it told.
          aria-busy={awaitingPlan ? "true" : undefined}
          onClick={onCta}
          className={cn(
            "mt-1 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60",
            def.highlighted
              ? "hover:brightness-110"
              : "bg-[#080B12] hover:brightness-110 dark:bg-foreground dark:text-background",
          )}
          style={def.highlighted ? { background: ACCENT } : undefined}
        >
          {isCurrent ? "Current plan" : def.cta}
        </button>

        <ul className="flex flex-col gap-2">
          {def.features.map((f) => (
            <li
              key={f}
              className="flex items-start gap-2 font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-foreground"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              {f}
            </li>
          ))}
        </ul>

        {/* Universal paid-plan perks (ticket): credit rollovers + on-demand
            top-ups. The rollover tooltip carries the exact rollover policy. */}
        {def.hasCreditDropdown && (
          <ul className="flex flex-col gap-2 border-t border-border/60 pt-3">
            <li className="flex items-start gap-2 font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="inline-flex items-center gap-1">
                Credit rollovers
                <span
                  className="cursor-help text-muted-foreground"
                  title="Credits roll over 1 month on monthly plans, or until 1 month after an annual plan ends."
                  aria-label="Credits roll over 1 month on monthly plans, or until 1 month after an annual plan ends."
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                </span>
              </span>
            </li>
            <li className="flex items-start gap-2 font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              On-demand credit top-ups
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}
